import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useCategories } from '../context/CategoriesContext';
import { useSettings } from '../context/SettingsContext';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useToast } from '../hooks/use-toast';
import { resolveImageUrl } from './ImageUpload';
import { NotifyWhenInStock } from './NotifyWhenInStock';
import { priceWithTier } from '../lib/pricing';
import Rial from './Rial';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeaturedProducts = () => {
  const [products, setProducts] = useState([]);
  const [qtyMap, setQtyMap] = useState({});
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollerRef = useRef(null);
  const { add } = useCart();
  const { discountPct, priceOverrides, email: customerEmail } = useCustomerAuth();
  const { categories } = useCategories();
  const { settings } = useSettings();
  const { toast } = useToast();

  useEffect(() => {
    if (!categories.length) return;
    axios
      .get(`${API}/products`)
      .then(({ data }) => {
        const picks = [];
        // Strict mode: only tagged products show up in Bestsellers. The
        // category fallback (`|| inCat[0]`) was removed so admins always
        // know exactly what appears here — tag = promote, untag = remove.
        categories.forEach((c) => {
          const inCat = data.filter((p) => p.category_slug === c.slug);
          const tagged = inCat.find((p) => p.tag);
          if (tagged) picks.push({ ...tagged, _category: c });
        });
        const extras = data
          .filter((p) => p.tag && !picks.find((x) => x.id === p.id))
          .slice(0, 6);
        extras.forEach((p) => {
          const cat = categories.find((c) => c.slug === p.category_slug);
          if (cat) picks.push({ ...p, _category: cat });
        });
        // Bubble "Coming Soon" items to the front so newest launches are
        // promoted, and demote "Out of stock" to the back.
        const rank = (p) => {
          const s = p.stock_status || (p.in_stock === false ? 'out_of_stock' : 'in_stock');
          return s === 'coming_soon' ? 0 : s === 'in_stock' ? 1 : 2;
        };
        picks.sort((a, b) => rank(a) - rank(b));
        setProducts(picks.slice(0, 12));
      })
      .catch(() => {});
  }, [categories]);

  const updateScrollButtons = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < maxScroll - 4);
  };

  useEffect(() => {
    updateScrollButtons();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [products]);

  const scrollByDir = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Scroll roughly one card width per click
    const firstCard = el.querySelector('[data-card]');
    const step = (firstCard?.offsetWidth || 240) + 16;
    el.scrollBy({ left: dir * step * 2, behavior: 'smooth' });
  };

  const handleAdd = (p) => {
    const status = p.stock_status || (p.in_stock === false ? 'out_of_stock' : 'in_stock');
    if (status === 'coming_soon') {
      toast({ title: 'Coming soon', description: `${p.name} isn’t available yet — we’ll launch it shortly.` });
      return;
    }
    if (status === 'out_of_stock' || p.in_stock === false) {
      toast({ title: 'Out of stock', description: `${p.name} is currently unavailable.` });
      return;
    }
    const raw = qtyMap[p.id];
    const qty = parseInt(raw, 10);
    const minQty = p.min_qty ?? 1;
    const uom = p.uom || 'ctn';
    if (!Number.isFinite(qty) || qty < 1) {
      toast({ title: 'Enter a quantity', description: `Type how many ${uom} to add.` });
      return;
    }
    if (qty < minQty) {
      toast({
        title: 'Minimum order quantity',
        description: `${p.name} requires at least ${minQty} ${uom}.`,
      });
      return;
    }
    add(
      {
        id: p.id,
        name: p.name,
        price: priceWithTier(p.price, discountPct, priceOverrides?.[p.id]),
        retail_price: p.price,
        size: p.size,
        size_type: p.size_type || 'size',
        image: resolveImageUrl(p.image) || resolveImageUrl(p._category.image),
        bg: p._category.bg,
        uom,
        min_qty: minQty,
        vat_applicable: !!p.vat_applicable,
      },
      qty,
    );
    setQtyMap((m) => ({ ...m, [p.id]: '' }));
    toast({
      title: 'Added to cart',
      description: `${qty} ${uom} × ${p.name} — OMR ${(priceWithTier(p.price, discountPct, priceOverrides?.[p.id]) * qty).toFixed(3)}`,
    });
  };

  if (products.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="flex items-end justify-between mb-6 sm:mb-8 gap-4">
        <div className="reveal min-w-0">
          <div className="text-[10px] sm:text-[11px] font-bold tracking-[0.2em] text-[#0078D4] uppercase mb-2">
            {settings?.featured_section_eyebrow || 'Bestsellers'}
          </div>
          <h2 className="layout-section-title font-display text-3xl sm:text-4xl lg:text-5xl text-slate-900">
            {settings?.featured_section_heading || 'Top moving SKUs'}
          </h2>
        </div>
        {/* Desktop slider controls */}
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => scrollByDir(-1)}
            disabled={!canScrollLeft}
            aria-label="Scroll left"
            className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-700 hover:bg-slate-50 hover:border-[#0078D4] hover:text-[#0078D4] transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-slate-200 disabled:hover:text-slate-700"
            data-testid="featured-scroll-left"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => scrollByDir(1)}
            disabled={!canScrollRight}
            aria-label="Scroll right"
            className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-700 hover:bg-slate-50 hover:border-[#0078D4] hover:text-[#0078D4] transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-slate-200 disabled:hover:text-slate-700"
            data-testid="featured-scroll-right"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Edge fade hints */}
      <div className="relative">
        {canScrollLeft && (
          <div className="absolute top-0 bottom-2 left-0 w-8 bg-gradient-to-r from-[#F8FAFC] to-transparent pointer-events-none z-10" />
        )}
        {canScrollRight && (
          <div className="absolute top-0 bottom-2 right-0 w-8 bg-gradient-to-l from-[#F8FAFC] to-transparent pointer-events-none z-10" />
        )}

        <div
          ref={scrollerRef}
          className="flex overflow-x-auto gap-4 pb-2 snap-x snap-mandatory scroll-smooth -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:thin]"
          style={{ scrollbarColor: '#cbd5e1 transparent' }}
          data-testid="featured-scroller"
        >
          {products.map((p) => {
            const uom = p.uom || 'ctn';
            const minQty = p.min_qty ?? 1;
            const status = p.stock_status || (p.in_stock === false ? 'out_of_stock' : 'in_stock');
            const comingSoon = status === 'coming_soon';
            const outOfStock = status === 'out_of_stock' || (p.in_stock === false && !comingSoon);
            const unavailable = comingSoon || outOfStock;
            return (
              <div
                key={p.id}
                data-card
                className="group bg-white rounded-2xl overflow-hidden card-lift flex flex-col snap-start shrink-0 w-[68%] xs:w-[55%] sm:w-[44%] md:w-[31%] lg:w-[24%] xl:w-[23%]"
                data-testid={comingSoon ? `featured-coming-soon-card-${p.id}` : outOfStock ? `featured-oos-card-${p.id}` : undefined}
              >
                <Link to={`/category/${p.category_slug}`} className="block">
                  <div
                    className="relative aspect-square flex items-center justify-center p-4 sm:p-6"
                    style={{ background: p._category.bg }}
                  >
                    {p.tag && !unavailable && (
                      <Badge
                        className="absolute top-3 left-3 text-[9px] sm:text-[10px] font-bold tracking-wide uppercase border-0 text-white"
                        style={{ background: p.tag_color || p._category.color }}
                      >
                        {p.tag}
                      </Badge>
                    )}
                    {comingSoon && (
                      <div className="absolute top-3 left-3 px-2 py-1 rounded-md text-[9px] sm:text-[10px] font-bold tracking-wider uppercase bg-amber-500 text-white shadow-sm">
                        Coming Soon
                      </div>
                    )}
                    {outOfStock && (
                      <div className="absolute top-3 left-3 px-2 py-1 rounded-md text-[9px] sm:text-[10px] font-bold tracking-wider uppercase bg-red-600 text-white shadow-sm">
                        Out of stock
                      </div>
                    )}
                    <img
                      src={resolveImageUrl(p.image) || resolveImageUrl(p._category.image)}
                      alt={p.name}
                      className={`max-h-full max-w-full object-contain transition-transform duration-500 ${unavailable ? '' : 'group-hover:scale-110'}`}
                    />
                  </div>
                </Link>
                <div className="p-3 sm:p-4 flex-1 flex flex-col">
                  <div className="text-[10px] font-semibold tracking-widest uppercase text-neutral-400 mb-0.5">
                    {p._category.name}
                  </div>
                  <div className="text-[13px] sm:text-[14px] font-semibold text-[#0F172A] leading-tight line-clamp-2 min-h-[2.2em] product-card-name">
                    {p.name}
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div>
                      <div className="font-bold text-[#0F172A] text-sm sm:text-base product-card-price">
                        {(() => {
                          const override = priceOverrides?.[p.id];
                          const hasOverride = override !== undefined && override !== null && Number(override) >= 0;
                          const effective = priceWithTier(p.price, discountPct, override);
                          const showStrike = hasOverride ? Number(override) < Number(p.price) : discountPct > 0;
                          if (showStrike) {
                            return (
                              <>
                                <span className="text-[11px] text-neutral-400 line-through font-medium mr-1 product-card-price-strike">
                                  <Rial /> {p.price.toFixed(3)}
                                </span>
                                <span className="text-emerald-600"><Rial /> {effective.toFixed(3)}</span>
                                <span className="ml-1 text-[10px] font-medium text-neutral-500 product-card-uom">/ {uom}</span>
                              </>
                            );
                          }
                          return (
                            <>
                              <Rial /> {effective.toFixed(3)}
                              <span className="ml-1 text-[10px] font-medium text-neutral-500 product-card-uom">/ {uom}</span>
                            </>
                          );
                        })()}
                      </div>
                      {p.size && (
                        <div className="text-[10px] text-neutral-500">
                          <span className="product-card-size">{p.size}</span> · <span className="product-card-minqty">Min {minQty} {uom}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {outOfStock ? (
                    <NotifyWhenInStock
                      productId={p.id}
                      defaultEmail={customerEmail || ''}
                      testId={`featured-oos-notify-${p.id}`}
                    />
                  ) : comingSoon ? (
                    <div className="mt-3 px-3 py-2 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-xs font-semibold text-center">
                      Coming soon — stay tuned
                    </div>
                  ) : (
                    <div className="mt-3 flex items-stretch gap-1.5">
                      <div className="flex-1 inline-flex items-center gap-1 bg-white border border-neutral-300 rounded-md px-2 h-9 focus-within:ring-2 focus-within:ring-[#0078D4] focus-within:border-transparent">
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="off"
                          placeholder="Qty"
                          value={qtyMap[p.id] || ''}
                          onChange={(e) =>
                            setQtyMap((m) => ({ ...m, [p.id]: e.target.value.replace(/[^\d]/g, '') }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAdd(p);
                            }
                          }}
                          className="h-7 min-w-0 w-full px-0 border-0 shadow-none focus-visible:ring-0 text-sm font-bold text-[#0F172A] text-left bg-transparent"
                          data-testid={`featured-qty-input-${p.id}`}
                        />
                        <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide whitespace-nowrap">
                          {uom}
                        </span>
                      </div>
                      <Button
                        onClick={() => handleAdd(p)}
                        size="sm"
                        className="h-9 w-9 p-0 rounded-md bg-[#0F172A] hover:bg-[#0078D4] text-white transition-colors"
                        aria-label="Add to cart"
                        data-testid={`featured-add-${p.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {/* Tail spacer so the last card can fully slide to the left edge on mobile */}
          <div className="shrink-0 w-2" aria-hidden="true" />
        </div>
      </div>

      {/* Mobile: subtle swipe hint */}
      <div className="sm:hidden mt-3 text-center text-[11px] text-neutral-400 font-medium">
        ← swipe to see more →
      </div>
    </section>
  );
};

export default FeaturedProducts;
